import { forwardRef } from "react";
import { Box, BoxProps, SxProps, Typography } from "@mui/material";
import useLocale from "src/domains/shared/hooks/localeHook/useLocale";
import { useTranslation } from "react-i18next";

/**
 * 프로젝트 헤더 바 props — `title` 만 있으면 강조, `subTitle` 도 있으면 "title | subTitle" 형식.
 *
 * `title` 누락 시 자동으로 "map.preview" i18n 키로 라벨 노출.
 */
export interface ProjectHeaderProps extends BoxProps {
  title?: string;
  subTitle?: string;
  sx?: SxProps;
}

export const ProjectHeader = forwardRef<HTMLDivElement, ProjectHeaderProps>(
  ({ title, subTitle, sx, ...props }, ref) => {
        const { t } = useTranslation("projects");
    const [locale] = useLocale();
    return (
      <Box ref={ref} sx={{ ...container, ...sx }} {...props}>
        {!title && (
          <Typography sx={normalText}>
            {t("map.preview", { ns: "projects" })}
          </Typography>
        )}
        <Typography sx={subTitle ? normalText : highlightText}>
          {title}
        </Typography>
        {subTitle && (
          <>
            <Typography sx={normalText}>|</Typography>
            <Typography sx={highlightText}>{subTitle}</Typography>
          </>
        )}
      </Box>
    );
  }
);

ProjectHeader.displayName = "ProjectHeader";

const container = {
  width: "100%",
  height: "45px",
  padding: "12px 24px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
} as const;

const highlightText = {
  fontSize: "14px",
  fontWeight: 600,
  overflow: "hidden",
  letterSpacing: "0.1px",
  textOverflow: "ellipsis",
  color: "rgba(0, 0, 0, 0.724)",
  display: "flex",
  alignItems: "center",
} as const;

const normalText = {
  fontSize: "14px",
  fontWeight: 500,
  overflow: "hidden",
  letterSpacing: "0.1px",
  textOverflow: "ellipsis",
  color: "rgba(0, 0, 0, 0.467)",
  display: "flex",
  alignItems: "center",
} as const;
